import { useEffect, useState } from 'react';
import { useActivity } from '../hooks/useActivity.js';
import { Drawer } from './ui';
import { timeAgoFine, formatDateTime } from '../util/time.js';

// T132 — the bell button + slide-over Activity panel, mounted in the shell next
// to the toast-History button. Surfaces the durable per-user event log (scan
// done, source errors, AI generations) so "what happened while I was away?" has
// an answer. Built on the shared <Drawer> primitive (T131) — focus trap +
// Escape + scrim for free. Level is shown icon + text, never colour alone.

const LEVEL = {
  success: { icon: '✓', cls: 'text-emerald-400', word: 'done' },
  error: { icon: '⚠', cls: 'text-red-400', word: 'error' },
  info: { icon: '✦', cls: 'text-slate-400', word: 'info' },
};

export function ActivityBellButton({ onNavigate }) {
  const { items, unreadCount, total, hasMore, refresh, markRead, loadMore } = useActivity();
  const [open, setOpen] = useState(false);

  // T055 deep-link — the OS notification onclick dispatches this to open the panel.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('hunter:open-activity', onOpen);
    return () => window.removeEventListener('hunter:open-activity', onOpen);
  }, []);

  // T281 review — OPENING REFETCHES. The rows are held in a module-level cache
  // fetched once behind `if (!_cache.loaded)`, and the 60s poll now asks only
  // for the unread COUNT. Without a fetch here, a tab left open overnight saw
  // the badge rise on the morning scan while the drawer still rendered last
  // night's page — and since markRead only sends ids it is holding, the new row
  // could never be marked either. The badge stuck at 1 until a full reload.
  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open, refresh]);

  // Opening = acknowledged, but only for what is on screen. T281: the badge
  // counts every unread row and this list shows 30, so a single open used to
  // mark all 327 read and destroy 297 the operator never saw. `markRead` sends
  // the ids it is holding, and nothing else is touched.
  //
  // Separate from the fetch above and keyed on `items`, so it also covers the
  // rows that arrive from it and from Load more. It terminates: markRead marks
  // the held rows read locally, so the next run finds nothing pending and
  // returns before issuing a request. Guarded on `open` because a background
  // poll must never mark anything — the operator has to be looking at it for
  // "read" to be true.
  useEffect(() => {
    if (!open) return;
    markRead();
  }, [open, items, markRead]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          onNavigate?.(); // close the mobile sidebar drawer if we're inside it
          setOpen(true);
        }}
        aria-label={`Activity${unreadCount ? `, ${unreadCount} unread` : ''}`}
        className="relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-400 transition hover:bg-slate-800/60 hover:text-slate-200"
      >
        <span className="w-4 text-xs" aria-hidden="true">
          🔔
        </span>
        <span>Activity</span>
        {unreadCount > 0 && (
          <span className="ml-auto rounded-full bg-brand-600/20 px-1.5 py-0.5 text-[10px] text-brand-400">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <Drawer
          open
          onClose={() => setOpen(false)}
          side="right"
          panelClassName="w-96 max-w-full"
          scrimClassName="bg-slate-950/50 backdrop-blur-[2px]"
          ariaLabel="Activity"
        >
          <header className="flex items-center justify-between gap-3 border-b border-slate-800 p-4">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-brand-500">Activity</p>
              <p className="text-xs text-slate-500">What happened while you were away</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="shrink-0 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200"
            >
              ✕
            </button>
          </header>

          <div className="flex-1 overflow-y-auto p-3">
            {items.length === 0 ? (
              <p className="px-1 py-6 text-center text-xs text-slate-500">
                Nothing yet — finished scans and AI generations show up here.
              </p>
            ) : (
              <ul className="space-y-1">
                {items.map((a) => {
                  const lv = LEVEL[a.level] ?? LEVEL.info;
                  return (
                    <li key={a.id} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 text-sm text-slate-200">
                          <span className={'mr-1 ' + lv.cls} aria-hidden="true">
                            {lv.icon}
                          </span>
                          {a.title}
                          <span className="sr-only"> ({lv.word})</span>
                        </p>
                        <span className="shrink-0 text-[10px] text-slate-500" title={formatDateTime(a.at)}>
                          {timeAgoFine(a.at)}
                        </span>
                      </div>
                      {a.detail && <p className="mt-0.5 text-xs text-slate-400">{a.detail}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
            {hasMore && (
              <div className="mt-3 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={loadMore}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
                >
                  Load more
                </button>
                <span className="text-xs text-slate-500 tabular-nums">
                  {items.length} of {total}
                </span>
              </div>
            )}
          </div>
        </Drawer>
      )}
    </>
  );
}
